import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './dashboard.css';
import Planner from '../dashboard/recipeengine'; // 
import Market from '../dashboard/market';

import PermissionGate from '../subscription/permissiongate';
import { useSubscription } from '../subscription/subscriptioncontext';
import SavedRecipes from '../dashboard/savedrecipes';
// Replace Chart.js imports with Recharts imports
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userInfo, setUserInfo] = useState({ firstName: 'User', email: '' });
  const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';
  const { subscription, getTrialRemaining, getTrialUsage, loading: subscriptionLoading } = useSubscription();
  
  // Fetch user information
  const fetchUserInfo = async () => {
    try {
      // First check if user info is stored in localStorage (from login)
      const storedUser = localStorage.getItem('user');
      const storedUserInfo = localStorage.getItem('userInfo');
      
      // Prioritize more complete userInfo if available, otherwise use basic user data
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        const userInfoData = {
          id: parsedUserInfo.id || parsedUserInfo.user_id,
          firstName: parsedUserInfo.firstName || parsedUserInfo.first_name || parsedUserInfo.firstname || 'User',
          lastName: parsedUserInfo.lastName || parsedUserInfo.last_name || parsedUserInfo.lastname || '',
          username: parsedUserInfo.username || '',
          email: parsedUserInfo.email || '',
          profilePicture: parsedUserInfo.profilePicture || parsedUserInfo.profile_picture || null,
          postalCode: parsedUserInfo.postalCode || parsedUserInfo.postal_code || ''
        };
        console.log('🔵 DASHBOARD - UserInfo loaded from userInfo localStorage:', userInfoData);
        setUserInfo(userInfoData);
        return;
      } else if (storedUser) {
        const parsedUserInfo = JSON.parse(storedUser);
        
        // Handle both camelCase and snake_case field names (prioritize camelCase from login)
        const firstName = parsedUserInfo.firstName || parsedUserInfo.first_name || parsedUserInfo.firstname || 'User';
        const email = parsedUserInfo.email || '';
        
        const userInfoFromLogin = {
          id: parsedUserInfo.id || parsedUserInfo.user_id,
          firstName: firstName,
          lastName: parsedUserInfo.lastName || parsedUserInfo.last_name || '',
          username: parsedUserInfo.username || '',
          email: email,
          profilePicture: parsedUserInfo.profilePicture || parsedUserInfo.profile_picture || null,
          postalCode: parsedUserInfo.postalCode || parsedUserInfo.postal_code || ''
        };
        
        console.log('🔵 DASHBOARD - UserInfo loaded from user localStorage:', userInfoFromLogin);
        setUserInfo(userInfoFromLogin);
        
        // If login data includes complete profile, also store it in userInfo for consistency
        if (parsedUserInfo.profilePicture || parsedUserInfo.profile_picture) {
          localStorage.setItem('userInfo', JSON.stringify(userInfoFromLogin));
          console.log('🔵 DASHBOARD - Stored complete profile in userInfo localStorage');
        }
        
        return;
      }

      // If not in localStorage, try to fetch from backend
      const response = await fetch(`${apiBaseUrl}/api/user-profile/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        credentials: 'include'  // Include cookies for session
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.success) {
          const normalizedUserInfo = {
            id: userData.user_id || userData.id,
            firstName: userData.first_name || userData.firstName || 'User',
            lastName: userData.last_name || userData.lastName || '',
            username: userData.username || '',
            email: userData.email || '',
            profilePicture: userData.profile_picture || null,
            postalCode: userData.postal_code || ''
          };
          
          setUserInfo(normalizedUserInfo);
          
          // Store normalized user info in localStorage for future use
          localStorage.setItem('userInfo', JSON.stringify(normalizedUserInfo));
          console.log('🔵 Dashboard UserInfo stored to localStorage:', normalizedUserInfo);
        }
      }
    } catch (error) {
      // Fallback to localStorage or default values
      const email = localStorage.getItem('userEmail') || '';
      setUserInfo({ firstName: 'User', email });
    }
  };

  // Check URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showPlanner = params.get('showPlanner');
    const accessToken = params.get('access_token');
    
    // If access_token is present, save it to localStorage
    if (accessToken) {
      localStorage.setItem('tiktok_access_token', accessToken);
    }
    
    // If showPlanner is true, set active section to planner
    if (showPlanner === 'true') {
      setActiveSection('planner');
    }
    
    // Clean up URL after processing parameters
    if (showPlanner || accessToken) {
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, [location.search]);

  // Fetch user info when component mounts
  useEffect(() => {
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);
  
  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.profile-avatar') && !event.target.closest('.profile-menu')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);
  
  // Convert data for Recharts format
  // Savings data
  const savingsData = [
    { name: 'Jan', value: 12 },
    { name: 'Feb', value: 19 },
    { name: 'Mar', value: 15 },
    { name: 'Apr', value: 22 },
    { name: 'May', value: 30 },
    { name: 'Jun', value: 25 },
    { name: 'Jul', value: 28 },
    { name: 'Aug', value: 35 },
    { name: 'Sep', value: 40 },
    { name: 'Oct', value: 38 },
    { name: 'Nov', value: 42 },
    { name: 'Dec', value: 50 }
  ];

  // Seasonal products data
  const seasonalProductsData = [
    { name: 'Jan', Citrus: 90, Berries: 10, 'Root Vegetables': 80, 'Leafy Greens': 30 },
    { name: 'Feb', Citrus: 85, Berries: 15, 'Root Vegetables': 70, 'Leafy Greens': 40 },
    { name: 'Mar', Citrus: 70, Berries: 25, 'Root Vegetables': 60, 'Leafy Greens': 60 },
    { name: 'Apr', Citrus: 50, Berries: 40, 'Root Vegetables': 40, 'Leafy Greens': 80 },
    { name: 'May', Citrus: 30, Berries: 80, 'Root Vegetables': 30, 'Leafy Greens': 90 },
    { name: 'Jun', Citrus: 20, Berries: 90, 'Root Vegetables': 20, 'Leafy Greens': 85 },
    { name: 'Jul', Citrus: 10, Berries: 95, 'Root Vegetables': 15, 'Leafy Greens': 80 },
    { name: 'Aug', Citrus: 5, Berries: 85, 'Root Vegetables': 20, 'Leafy Greens': 75 },
    { name: 'Sep', Citrus: 10, Berries: 60, 'Root Vegetables': 40, 'Leafy Greens': 65 },
    { name: 'Oct', Citrus: 30, Berries: 30, 'Root Vegetables': 70, 'Leafy Greens': 50 },
    { name: 'Nov', Citrus: 60, Berries: 15, 'Root Vegetables': 85, 'Leafy Greens': 40 },
    { name: 'Dec', Citrus: 80, Berries: 10, 'Root Vegetables': 90, 'Leafy Greens': 30 }
  ];

  // Trending deals data
  const trendingDealsData = [
    { name: 'Organic Milk', value: 85 },
    { name: 'Chicken Breast', value: 78 },
    { name: 'Avocados', value: 74 },
    { name: 'Greek Yogurt', value: 70 },
    { name: 'Pasta', value: 65 },
    { name: 'Coffee Beans', value: 60 },
    { name: 'Bananas', value: 55 }
  ];

  // Cuisine data
  const cuisineData = [
    { name: 'Italian', value: 30 },
    { name: 'Mexican', value: 25 },
    { name: 'Asian', value: 15 },
    { name: 'American', value: 12 },
    { name: 'Mediterranean', value: 10 },
    { name: 'Indian', value: 8 }
  ];

  // Colors for pie chart
  const CUISINE_COLORS = [
    '#FF6384', // red
    '#36A2EB', // blue
    '#FFCE56', // yellow
    '#4BC0C0', // teal
    '#9966FF', // purple
    '#FF9F40'  // orange
  ];

  // Colors for bar chart
  const SEASONAL_COLORS = {
    'Citrus': '#FF9F40',
    'Berries': '#9966FF',
    'Root Vegetables': '#FF6384',
    'Leafy Greens': '#4BC0C0'
  };
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  // Handle profile picture upload
  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (PNG, JPG, etc.)');
        return;
      }

      // Check file size (max 1MB for profile pictures)
      if (file.size > 1 * 1024 * 1024) {
        alert('Image size must be less than 1MB');
        return;
      }

      // Create canvas for compression (smaller for profile pics)
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = async () => {
        // Set canvas dimensions (max 150x150 for profile pictures)
        const maxSize = 150;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress the image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        try {
          // Update profile picture via API
          const response = await fetch(`${apiBaseUrl}/api/update-profile-picture/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_id: userInfo.id || JSON.parse(localStorage.getItem('user') || '{}').id,
              profile_picture: compressedDataUrl
            })
          });

          const data = await response.json();
          if (response.ok && data.success) {
            // Update local state
            const updatedUserInfo = {
              ...userInfo,
              profilePicture: compressedDataUrl
            };
            setUserInfo(updatedUserInfo);
            
            // Update localStorage to persist the profile picture
            localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
            
            // Dispatch custom event to notify other components
            window.dispatchEvent(new CustomEvent('userInfoUpdated'));
            
            console.log('Profile picture updated successfully');
          } else {
            console.error('Failed to update profile picture:', data.error);
            alert('Failed to update profile picture. Please try again.');
          }
        } catch (error) {
          console.error('Error updating profile picture:', error);
          alert('Failed to update profile picture. Please try again.');
        }
      };
      
      // Load the image
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const navigateTo = (section) => {
    if (section === 'deals' || section === 'price' || section === 'subscription') {
      navigate(`/${section}`);
    } else {
      setActiveSection(section);
    }
    setShowProfileMenu(false);
  };

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'planner':
        return (
          <PermissionGate 
            feature="recipe_engine"
          >
            <Planner />
          </PermissionGate>
        );
      case 'market':
        return <Market />;

      case 'savedrecipes':
        return <SavedRecipes />;
      case 'dashboard':
      default:
        return (
        <>
          <div className="dashboard-header">
            <h1>Welcome, {userInfo.firstName}</h1>
          </div>
          <div className="dashboard-welcome">
            <p>Manage your grocery planning and track your savings all in one place.</p>  
            <div className="dashboard-cards">
              {subscriptionLoading ? (
                <>
                  <div className="dashboard-card dashboard-card-shimmer" />
                  <div className="dashboard-card dashboard-card-shimmer" />
                  <div className="dashboard-card dashboard-card-shimmer" />
                </>
              ) : (
                <>
                  <div className="dashboard-card" onClick={() => setActiveSection('planner')}>
                    <div className="card-icon">
                        📅
                    </div>
                    <h3>Recipe Engine</h3>
                    <p>Plan your meals for the week and generate smart shopping lists with optimized routes automatically.</p>
                    {subscription?.status === 'trial' && (
                      <div className="trial-badge">
                        Trial Feature
                      </div>
                    )}
                  </div>

                  <div className="dashboard-card" onClick={() => setActiveSection('savedrecipes')}>
                    <div className="card-icon">
                        📚
                    </div>
                    <h3>Saved Recipes</h3>
                    <p>Access your saved recipes and discover new ones based on deals or through markets.</p>
                    {subscription?.status === 'trial' && (
                      <div className="trial-badge">
                        Trial Feature
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="dashboard-insights">
            <div className="dashboard-header">
              <h1>Insights</h1>
            </div>
            
            {/* Insights Graphs Section */}
            <div className="insights-container">
              {/* Top row - Savings and Seasonal Products */}
              <div className="insights-row">
                {/* Savings Graph */}
                <div className="insights-card">
                  <h3>Total Savings</h3>
                  <p>Your accumulated savings with Penny</p>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={savingsData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          name="Savings ($)" 
                          stroke="#0B303B" 
                          fill="rgba(11, 48, 59, 0.2)" 
                          activeDot={{ r: 8 }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Seasonal Products Graph */}
                <div className="insights-card">
                  <h3>Seasonal Products</h3>
                  <p>What's in season throughout the year</p>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={seasonalProductsData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Citrus" stackId="a" fill={SEASONAL_COLORS['Citrus']} />
                        <Bar dataKey="Berries" stackId="a" fill={SEASONAL_COLORS['Berries']} />
                        <Bar dataKey="Root Vegetables" stackId="a" fill={SEASONAL_COLORS['Root Vegetables']} />
                        <Bar dataKey="Leafy Greens" stackId="a" fill={SEASONAL_COLORS['Leafy Greens']} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              
              {/* Bottom row - Trending Deals and Cuisine Preferences */}
              <div className="insights-row">
                {/* Trending Deals Graph */}
                <div className="insights-card">
                  <h3>Trending Deals</h3>
                  <p>Most popular products on sale right now</p>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={trendingDealsData}
                        margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis 
                          type="category" 
                          dataKey="name" 
                          width={100}
                          tick={{ 
                            fontSize: 11,
                            width: 100,
                            wordWrap: 'break-word',
                            textAnchor: 'end'
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          dataKey="value" 
                          name="Popularity Score" 
                          fill="#0B303B" 
                          background={{ fill: '#eee' }}
                        >
                          {trendingDealsData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={`rgba(11, 48, 59, ${0.8 - (index * 0.1)})`} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Cuisine Preferences Graph */}
                <div className="insights-card">
                  <h3>Cuisine Preferences</h3>
                  <p>Your shopping patterns by cuisine type</p>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={cuisineData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="name"
                        >
                          {cuisineData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CUISINE_COLORS[index % CUISINE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value}%`, 'Preference']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="dashboard-market">
            <div className="dashboard-header">
              <h1>Market</h1>
            </div>
            <div className="dashboard-welcome">
              <p>Need inspiration? Check out the others work</p>
              <div className="dashboard-cards">
                <div className="dashboard-card" onClick={() => setActiveSection('market')}>
                    <div className="card-icon">
                          🛒
                    </div>
                    <h3>Market</h3>
                    <p>Head over to market and find the best recipes and deals created by others</p>
                </div>
              </div>
            </div>
          </div>
        </>
        );
    }
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className={`dashboard-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <img src="/images/Pennylogga.png" alt="Penny Logo" className="sidebar-logo" />
          <button className="toggle-button" onClick={toggleSidebar}>
            {isSidebarOpen ? '←' : '→'}
          </button>
        </div>
        
        <div className="sidebar-menu">
          <div 
            className={`menu-item ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveSection('dashboard')}
          >
            <span>Dashboard</span>
          </div>
          
          <div 
            className={`menu-item ${activeSection === 'planner' ? 'active' : ''}`}
            onClick={() => setActiveSection('planner')}
          >
            
            <span>Recipe Engine</span>
          </div>
          


          <div 
            className={`menu-item ${activeSection === 'savedrecipes' ? 'active' : ''}`}
            onClick={() => setActiveSection('savedrecipes')}
          >
            
            <span>Saved Recipes</span>
          </div>

          <div 
            className={`menu-item ${activeSection === 'market' ? 'active' : ''}`}
            onClick={() => setActiveSection('market')}
          >
              
            <span>Market</span>
          </div>
        </div>
        
        <div className="sidebar-footer">
          <div className="menu-item" onClick={() => navigateTo('subscription')}>
            
            <span>Show Subscription</span>
          </div>
          <div className="menu-item">
            <span>Settings</span>
          </div>
          
        </div>
      </div>
      
      {/* Toggle button for closed sidebar */}
      {!isSidebarOpen && (
        <button className="sidebar-expand-button" onClick={toggleSidebar}>
          ☰
        </button>
      )}
      
      {/* Profile Avatar and Trial Info - Top right */}
      <div style={{ position: 'absolute', top: 24, right: 32, display: 'flex', alignItems: 'center', gap: '16px', zIndex: 10 }}>
        {subscription && subscription.status === 'trial' && (
          <div style={{
            background: 'linear-gradient(90deg, #ffc107, #ff9800)',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '12px',
            padding: '4px 12px',
            fontSize: '13px',
            display: 'inline-block',
            letterSpacing: '0.5px',
            marginRight: '0',
          }}>
            Trial: {getTrialUsage()} uses ({getTrialRemaining()} left)
          </div>
        )}
        {subscription && subscription.status === 'active' && (
          <div style={{
            background: 'linear-gradient(90deg, #28a745, #20c997)',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '12px',
            padding: '4px 12px',
            fontSize: '13px',
            display: 'inline-block',
            letterSpacing: '0.5px',
            marginRight: '0',
          }}>
            {subscription.plan_name || 'Premium'}
          </div>
        )}
        <div className="profile-avatar" onClick={toggleProfileMenu}>
          {userInfo.profilePicture ? (
            <img 
              src={userInfo.profilePicture} 
              alt="Profile" 
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: '#28a745',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {userInfo.firstName ? userInfo.firstName.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
        </div>
      </div>
      
      {/* Profile Menu Dropdown */}
      {showProfileMenu && (
        <div className="profile-menu">
          <div className="profile-header">
            
            <div className="profile-info">
              <h4>{userInfo.firstName}</h4>
              <p>{userInfo.email}</p>
              {/* Only show plan badge in profile menu now, with correct color for plan */}
              {subscription && (
                <div style={{
                  background: subscription.status === 'active'
                    ? 'linear-gradient(90deg, #28a745, #20c997)'
                    : 'linear-gradient(90deg, #ffc107, #ff9800)',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  padding: '4px 12px',
                  fontSize: '13px',
                  margin: '10px 0 0 0',
                  display: 'inline-block',
                  letterSpacing: '0.5px',
                  textTransform: 'capitalize',
                }}>
                  {subscription.plan_name || (subscription.status === 'active' ? 'Premium' : 'Free Trial')}
                </div>
              )}
            </div>
          </div>
          <div className="profile-menu-items">
            <div className="profile-menu-item" onClick={() => document.getElementById('profile-picture-input').click()}>
              <span>Change Profile Picture</span>
            </div>
            <div className="profile-menu-item" onClick={() => navigateTo('profile')}>
              
              <span>Profile Settings</span>
            </div>
            <div className="profile-menu-item" onClick={() => navigateTo('subscription')}>
              <span>{subscription && subscription.status === 'active' ? 'Handle Subscription' : 'Upgrade Subscription'}</span>
            </div>
            <div className="profile-menu-item" onClick={() => navigateTo('help')}>
              
              <span>Help & Support</span>
            </div>
            <div className="profile-menu-divider"></div>
            <div className="profile-menu-item logout" onClick={async () => {
              // Call backend logout to clear Django session
              try {
                await fetch(`${apiBaseUrl}/api/logout/`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                  }
                });
              } catch (error) {
                console.log('Logout API call failed:', error);
              }
              
              // Clear frontend storage
              localStorage.removeItem('isLoggedIn');
              localStorage.removeItem('user');
              localStorage.removeItem('userInfo');
              
              navigate('/login');
            }}>
              
              <span>Logout</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className={`dashboard-content ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {renderContent()}
      </div>

      {/* Hidden file input for profile picture upload */}
      <input
        id="profile-picture-input"
        type="file"
        accept="image/*"
        onChange={handleProfilePictureUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default Dashboard;